import type { RelativeDateRange } from 'components/datepicker/types';
import type { ExportWorksheet } from 'components/superreports/ReportExport/types';
import { EMPTY_ARRAY } from 'constants/defaults';
import type { HourColumn, SupportedExtensions, TaskColumn } from 'constants/export';
import type { ExtendedError } from 'factories/api';
import { createBatchActionsModule } from 'redux/reusable/batchSelections';
import type { XhrStatus, XhrStatusUIState } from 'redux/reusable/xhrStatus';
import { createXhrStatusModule } from 'redux/reusable/xhrStatus';
import type { Currency } from 'types/account';
import type { DateTypePeriodNames } from 'types/dates';
import type { Hour, HourTimestamp } from 'types/hour';
import type {
  BudgetCalculationStatus,
  BudgetScope,
  BudgetType,
  FilterGroupValues,
  LightProject,
  Project,
  ProjectBase,
} from 'types/project';
import type {
  DownloadedReportFile,
  EntityDataKey,
  EntityDataWithTransformedTotals,
  PublicReportEntity,
  ReportBase,
  ReportEntity,
  ReportFilter,
  SharedReportsFilter,
  WidgetMetadata,
} from 'types/reports';
import type { Tag, TagBase, TagFilterMode } from 'types/tag';
import type { Cost, Duration } from 'types/units';
import type { User } from 'types/user';
import type { DeepPartial } from 'types/utility';
import { actionCreatorFactory } from 'typescript-fsa';
import type { CostAndDurationSummary } from 'utils/summary';
import type { ColumnModeOptions } from './accounts.types';
import type { HourState } from './hourStates.schema';

export const { wrapFetching, wrapSaving, withXhrStatusReducer, abortFetching } =
  createXhrStatusModule('superreports');

export const { batchSelectionActions, withBatchSelectionsReducer } =
  createBatchActionsModule<State['selection']>('superreports-selection');

// -------------------------------------------------------------------------------------------------
// FSA

const actionCreator = actionCreatorFactory('timely/superreports');

// Reports
export const fetchedReports = actionCreator<{ data: Array<ReportEntity>; reload?: boolean }>(
  'FETCHED_REPORTS'
);
export const fetchedReport = actionCreator<ReportEntity>('FETCHED_REPORT');
export const createdNewReport = actionCreator<unknown>('CREATED_NEW_REPORT');
export const markReportDeleted = actionCreator<number>('MARK_REPORT_DELETED');
export const markReportUndeleted = actionCreator<number>('MARK_REPORT_UNDELETED');
export const deleteReport = actionCreator<number>('DELETE_REPORT');
export const setReportFilter = actionCreator<Partial<ReportFilter>>('SET_REPORT_FILTER');
export const fetchedReportData = actionCreator<{
  data: EntityDataWithTransformedTotals;
  reload?: boolean;
}>('FETCHED_REPORT_DATA');
export const clearEntityFetchStatus = actionCreator('CLEAR_ENTITY_FETCH_STATUS');

// Report export
export const fetchedReportExportSettings = actionCreator<ReportExportSettingsPayload>(
  'FETCHED_REPORT_EXPORT_SETTINGS'
);
export const fetchedNextExportDate = actionCreator<{ scheduled_at: string }>(
  'FETCHED_NEXT_EXPORT_DATE'
);

export const receiveReportExportFile = actionCreator<DownloadedReportFile>(
  'RECEIVE_REPORT_EXPORT_FILE'
);

// Drafts
export const clearReportDraft = actionCreator('CLEAR_REPORT_DRAFT');
export const createReportDraft = actionCreator<ReportDraft>('CREATE_REPORT_DRAFT');
export const dismissEmailedReport = actionCreator('DISMISS_EMAILED_REPORT');
export const emailedReport = actionCreator<EmailedReport | null | undefined>('EMAILED_REPORT');
export const setDraftSignature = actionCreator('SET_DRAFT_SIGNATURE');

export const setQuery = actionCreator<DeepPartial<ReportQueryExtended>>('SET_QUERY');

export const updateReportDraft = actionCreator<Partial<ReportDraft>>('UPDATE_REPORT_DRAFT');
export const updatedExistingReport = actionCreator<unknown>('UPDATED_EXISTING_REPORT');

// Public reports
export const fetchedPublicReports =
  actionCreator<Array<PublicReportEntity>>('FETCHED_PUBLIC_REPORT');
export const createdNewPublicReport = actionCreator<unknown>('CREATED_NEW_PUBLIC_REPORT');
export const setPublicReportFilter = actionCreator<Partial<SharedReportsFilter>>(
  'SET_PUBLIC_REPORT_FILTER'
);
export const deletePublicReport = actionCreator<number>('DELETE_PUBLIC_REPORT');
export const undeletePublicReport = actionCreator<number>('UNDELETE_PUBLIC_REPORT');
export const togglePublicReportView = actionCreator<ReportViewTypes | null>(
  'TOGGLE_PUBLIC_REPORT_VIEW'
);
export const publicReportListFilter = actionCreator<ReportFilterTypes | null>(
  'PUBLIC_REPORT_LIST_FILTER'
);
export const setPublicReportTimestamp = actionCreator<string>('SET_PUBLIC_REPORT_TIMESTAMP');

// Widgets
export const addWidgetToDraft = actionCreator<WidgetDraft>('ADD_WIDGET_TO_DRAFT');
export const changeWidgetType = actionCreator<{
  widgetType: string;
  widgetReference: string | number | undefined;
}>('CHANGE_WIDGET_TYPE');
export const collapseRowOnWidget = actionCreator<{
  rowId: number;
  widgetReference: string | number;
}>('COLLAPSE_ROW_ON_WIDGET');
export const editWidgetOnDraft = actionCreator<WidgetDraft>('EDIT_WIDGET_ON_DRAFT');
export const expandAndLoadRowOnWidget = actionCreator<{
  rowId: number;
  widgetReference: string | number;
}>('EXPAND_AND_LOAD_ROW_ON_WIDGET');
export const fetchedWidgetHours = actionCreator<{
  rowId: number;
  widgetReference: string | number;
  hasMoreResults: boolean;
  hourIds: Array<number>; // Hour IDs loaded for this row (used by linked_work widget)
}>('FETCHED_WIDGET_HOURS');
export const fetchWidgetHoursError = actionCreator<{
  rowId: number;
  widgetReference: string | number;
}>('FETCH_WIDGET_HOURS_ERROR');
export const increaseSequenceStartingWith = actionCreator<number>(
  'INCREASE_SEQUENCE_STARTING_WITH'
);
export const decreaseSequenceStartingWith = actionCreator<number>(
  'DECREASE_SEQUENCE_STARTING_WITH'
);
export const removeWidgetOnDraft = actionCreator<string | number | undefined>(
  'REMOVE_WIDGET_ON_DRAFT'
);
export const resetWidgetStatusOnDraft = actionCreator('RESET_WIDGET_STATUS_ON_DRAFT');
export const updateWidgetColumns = actionCreator<UpdateWidgetColumns>('UPDATE_WIDGET_COLUMNS');
export const updateWidgetOptions = actionCreator<{
  reference?: number | string;
  options: Partial<WidgetOptions>;
}>('UPDATE_WIDGET_OPTIONS');
export const moveWidget = actionCreator<MoveWidgetParams>('MOVE_WIDGET');
export const fetchedWidgetMetadata =
  actionCreator<Array<WidgetMetadata>>('FETCHED_WIDGET_METADATA');
export const synthesizeProjectsFromHours = actionCreator<Array<Hour<Project | LightProject>>>(
  'SYNTHESIZE_PROJECTS_FROM_HOURS'
);
export const enforceMinimumWidgetWidth = actionCreator<number>('ENFORCE_MINIMUM_WIDGET_WIDTH');

// Timesheets
export const saveTimesheetQuery = actionCreator('SAVE_TIMESHEET_QUERY');
export const restoreTimesheetQuery = actionCreator('RESTORE_TIMESHEET_QUERY');
export const fetchedTimesheetData = actionCreator<boolean>('FETCHED_TIMESHEET_DATA');
export const retrieveTotalHoursCount = actionCreator<number>('RETRIEVE_TOTAL_HOURS_COUNT');
export const setTimesheetColumns = actionCreator<Array<HourColumn>>('SET_TIMESHEET_COLUMNS');
export const setTimesheetTaskColumns = actionCreator<Array<TaskColumn>>(
  'SET_TIMESHEET_TASK_COLUMNS'
);
export const saveClientReportQuery = actionCreator('SAVE_CLIENT_REPORT_QUERY');
export const restoreClientReportQuery = actionCreator('RESTORE_CLIENT_REPORT_QUERY');
export const setClientReportQuery =
  actionCreator<DeepPartial<ReportQuery>>('SET_CLIENT_REPORT_QUERY');

// -------------------------------------------------------------------------------------------------

export const fetchedHourSummaryData = actionCreator<boolean>('FETCHED_HOUR_SUMMARY_DATA');

export type ReportViewTypes = 'list' | 'grid';
export type ReportFilterTypes = 'pinned-and-recent' | 'all';
export type ReportProjectIds = Array<number> | 'all' | 'active' | 'archived';

export interface ReportQuery {
  from: string | undefined;
  to: string | undefined;
  span: DateTypePeriodNames | undefined;
  filters: {
    project_ids: ReportProjectIds;
    team_ids: Array<number>;
    user_ids: Array<number>;
    label_ids: Array<number>;
    state_ids: Array<number>;
    billed: null | boolean;
    locked: null | boolean;
    filter_group?: FilterGroupValues;
    label_filter?: TagFilterMode;
    resource?: string | undefined;
    external_link_filter?: string; // 'with_links' | 'without_links' | 'provider:jira' | etc
    sync_status_filter?: string; // 'synced' | 'pending' | 'failed' | 'auth_required' | etc
  };
  limit?: number;
  page?: number;
  sort?: string;
  customRangeEnabled?: boolean;
  order?: 'asc' | 'desc';
  group_only?: boolean;
  client_ids?: Array<number>;
}

type SetToUndefined<T, K extends keyof T> = Omit<T, K> & {
  [_P in K]: undefined;
};

type ReportQueryWithRelativeDateRange = SetToUndefined<ReportQuery, 'from' | 'to'> & {
  relativeDateRange: RelativeDateRange;
};

export type ReportQueryExtended = ReportQuery & {
  relativeDateRange?: RelativeDateRange;
};

export type ReportFiltersWithoutLegacyProps = Omit<ReportQuery['filters'], 'billed' | 'locked'>;
export interface ReportQueryWithoutLegacyFilters extends Omit<ReportQueryExtended, 'filters'> {
  filters: ReportFiltersWithoutLegacyProps;
  client_ids?: Array<number>;
  include_linked_metadata?: boolean;
}

export interface ReportSelection {
  hourIds: Array<number>;
  rangeStartHourId: number | null;
  rangeEndHourId: number | null;
  labelIds: Array<number>;
  billedState: boolean | null;
  projectId: number | null;
  lockedState: null | boolean;
  stateId?: number;
}

export interface ColumnsProps {
  names: Array<string>;
  order: 'asc' | 'desc';
  sort_by: string;
}

export interface ColumnsStatus {
  expandedRowIds: Array<number>;
  loadedRowIds: Array<number>;
  loadingRowIds: Array<number>;
  hourIdsByRowId: Record<number, Array<number>>; // For linked_work widget to track hours per row
}

export interface WidgetEntity {
  columns: ColumnsProps;
  id?: number;
  options?: WidgetOptions;
  report_id?: number;
  sequence: number;
  title: string;
  widget_type: string;
  threshold_factor?: number;
}

export type WidgetReference = string | number;

export enum WidgetBarScale {
  Default = 'default',
  Day = 'day',
  Week = 'week',
  Month = 'month',
  Year = 'year',
}

export interface WidgetOptions {
  barScale?: WidgetBarScale;
  dataType?: WidgetDataType;
  showBillableNonBillable?: boolean;
  showMedian?: boolean;
  showSummaries?: boolean;
}

export interface WidgetDraft extends WidgetEntity {
  _deleted?: boolean;
  _ref?: string;
  _status: ColumnsStatus;
}

export interface CommonDataEntity {
  billed_cost?: Cost;
  billed_duration?: Duration;
  cost?: Cost;
  duration?: Duration;
  estimated_cost?: Cost;
  estimated_duration?: Duration;
  unbilled_cost?: Cost;
  unbilled_duration?: Duration;
  billable_duration?: Duration;
  non_billable_duration?: Duration;
  internal_cost?: Cost;
  profit?: Cost;
  profitability?: number;
}

export const CommonDataEntityKeys: ReadonlyArray<keyof CommonDataEntity> = [
  'billed_cost',
  'billed_duration',
  'cost',
  'duration',
  'estimated_cost',
  'estimated_duration',
  'unbilled_cost',
  'unbilled_duration',
  'billable_duration',
  'non_billable_duration',
];

export interface ClientDataEntity<TProject = number> extends CommonDataEntity {
  id: number;
  name: string;
  projects: Array<TProject>;
}

export interface ClientDataEntities {
  [id: number]: ClientDataEntity;
}

export interface DayDataEntity extends CommonDataEntity {
  day: string;
  label: string;
}
export interface DayDataEntities {
  [id: string]: DayDataEntity;
}

export interface BudgetDataEntity {
  budget?: number;
  budget_percent?: number;
  budget_progress?: number;
  budget_calculation?: BudgetCalculationStatus;
  budget_scope?: BudgetScope;
  budget_type?: BudgetType;
  has_recurrence?: boolean;
}

export interface CapacityDataEntity {
  capacity?: Duration;
  overtime?: Duration;
  undertime?: Duration;
}

export interface ProjectDataEntity extends CommonDataEntity, BudgetDataEntity {
  id: number;
  name: string;
  color: string;
  client?: {
    name: string;
  };
}

export interface ProjectDataEntities {
  [id: number]: ProjectDataEntity;
}

export interface UserDataEntity extends User, CommonDataEntity, CapacityDataEntity {}
export interface TagDataEntity extends Omit<Tag, 'children'>, CommonDataEntity {
  children: Array<number>;
}

export interface TeamDataEntity extends CommonDataEntity, CapacityDataEntity {}

export interface LinkedWorkDataEntity extends CommonDataEntity {
  id: number;
  uri: string;
  external_code: string;
  title: string;
  name: string;
  html_url?: string;
  provider_type: string;
  internal_cost?: Cost;
  profit?: Cost;
  profitability?: number;
}

export interface ReportEntities {
  [id: number]: ReportEntity;
}

export type WidgetMetadataDictionary = Record<
  string,
  {
    columns: Array<string>;
    entity: EntityDataKey;
    title: string;
  }
>;

export interface State {
  clientDataEntities: ClientDataEntities;
  clientDataResult: Array<number>;
  dayDataEntities: DayDataEntities;
  dayDataResult: Array<string>;
  draft: ReportDraft;
  draftSignature?: {} | null;
  emailedReport?: EmailedReport | null;
  hourSummaryTotalCount: number;
  hourSummaryMaybeHasMore: boolean;
  labelDataEntities: { [id: number]: TagDataEntity };
  labelDataResult: Array<number>;
  projectDataEntities: ProjectDataEntities;
  projectDataResult: Array<number>;
  publicReportEntities: { [id: number]: PublicReportEntity };
  publicReportFilter: SharedReportsFilter;
  publicReportResult: Array<number>;
  publicReportTimestamp: string;
  publicReportMaybeHasMore: boolean;
  query: ReportQuery | ReportQueryWithRelativeDateRange;
  reportsMaybeHasMore: boolean;
  reportEntities: ReportEntities;
  reportFilter: ReportFilter;
  reportListFilter: ReportFilterTypes;
  reportResult: Array<number>;
  reportView: ReportViewTypes;
  reportExportSettings: ReportExportSettingsPayload;
  selection: ReportSelection;
  teamDataEntities: { [id: number]: TeamDataEntity };
  teamDataResult: Array<number>;
  linkedWorkDataEntities: { [id: number]: LinkedWorkDataEntity };
  linkedWorkDataResult: Array<number>;
  timesheetColumns: Array<HourColumn>;
  timesheetTaskColumns: Array<TaskColumn>;
  timesheetTotalCount: number;
  timesheetMaybeHasMore: boolean;
  timesheetQuery: ReportQuery;
  clientReportQuery: ReportQuery;
  totals: CostAndDurationSummary;
  userDataEntities: { [id: number]: UserDataEntity };
  userDataResult: Array<number>;
  widgetMetadata: WidgetMetadataDictionary;
  xhrStatus: XhrStatus;
  minimumWidgetWidth: number;
  nextExportDate?: string;
}

export interface AdditionalWidgetRowValues {
  maxValues: {
    minutesLogged?: number;
  };
  totals?: CostAndDurationSummary;
  currency: Currency;
  rows?: Array<GenericWidgetRow>;
  hours?: Array<GenericWidgetRow>;
  indentedRow?: boolean;
  projects?: Array<any>;
  isSelected?: boolean;
  hasMultipleCurrencies?: boolean;
}

export interface GenericWidgetRowValues {
  id?: number;
  uri?: string; // For linked_work widget rows
  resource?: string;
  name?: string;
  avatar?: string | null;
  emoji?: string | null;
  color?: string | null;

  loggedMinutes?: number;
  plannedMinutes?: number;
  loggedPercentage?: number;
  loggedMoney?: string;
  plannedMoney?: string;
  billedMinutes?: number;
  billedMoney?: string;
  unbilledMinutes?: number;
  unbilledMoney?: string;
  billableHours?: number;
  nonBillableHours?: number;

  budgetPercent?: number;
  budgetProgress?: number;
  budgetType?: BudgetType;
  budgetValue?: number;
  budgetIsPending?: boolean;

  capacity?: number;
  overtime?: number;
  undertime?: number;
}

// extracted from ReportTableBodyRow:
export interface HourWidgetRowValues {
  description?: string;
  from?: string | null;
  to?: string | null;
  timestamps?: Array<Pick<HourTimestamp, 'id' | 'from' | 'to'>>;
  isSelected?: boolean;
  date?: string;
  labels?: Array<TagBase>;
  isBilled?: boolean;
  isInvoiced?: boolean;
  isLocked?: boolean;
  isDayLocked?: boolean;
  project?: ProjectBase;
  /**
   * If `true`, the user can manage (update/delete) this hour.
   */
  canManage?: boolean;
  isHour?: boolean;
  billedMinutes?: number;
  billedMoney?: string;
  unbilledMinutes?: number;
  unbilledMoney?: string;
  internalCost?: string;
  profit?: string;
  profitability?: number;
  markedAsUnbillable?: boolean;
  state?: HourState;
}

// The type used in ReportTableBodyRow's .row prop
export type GenericWidgetRow = GenericWidgetRowValues &
  HourWidgetRowValues &
  Partial<AdditionalWidgetRowValues>;

// Type returned by factory
export interface WidgetWithData<TData = any, TMetadata = any> extends WidgetDraft {
  data?: Array<TData>;
  dataEntities?: Array<EntityDataKey>;
  metadata?: TMetadata;
  rows?: Array<GenericWidgetRow>;
  footer?: GenericWidgetRow;
  headers?: Array<{
    column: string;
    active: boolean;
    name: string;
  }>;
}

export interface ReportDraft extends Partial<ReportBase> {
  createdAt?: string;
  events?: boolean;
  hasLoadedInitialData?: boolean;
  live?: boolean;
  multiCurrency?: boolean;
  widgets: Array<WidgetDraft>;
}

export interface EmailedReport {
  id: number;
  email: string;
  sent_via_email?: boolean;
}

export interface ReportDetailsPageProps extends XhrStatusUIState {
  query: ReportQuery;
  totals: CostAndDurationSummary;
  draft: ReportDraft;
  hasExistingReportDraft: boolean;
  widgets: Array<WidgetWithData>;
  entities: Array<EntityDataKey>;
  isLoadingEntities: Record<EntityDataKey, boolean>;
  isLoadingWidgetMetadata: boolean;
  widgetMetadata: WidgetMetadataDictionary;
  ui: {
    showMoney: boolean;
    isShowingBatchActions: boolean;
    showAddWidgetHint: boolean;
    emailedReport?: EmailedReport | null;
    customAssetRevision?: string;
    isSavingOrExporting: boolean;
    isAppInitialized: boolean;
    isBudgetFeatureEnabled: boolean;
    minimumWidgetWidth: number;
    hasMultipleCurrencies: boolean;
  };
}

export interface DataItemBillingSummary {
  billedAmount?: number;
  billedPercent?: number;
  unbilledAmount?: number;
  unbilledPercent?: number;
  billableAmount?: number;
  billablePercent?: number;
  nonBillableAmount?: number;
  nonBillablePercent?: number;
}

export interface DonutDataItem {
  name: string;
  value: number;
  percentOfTotal: number;
  mergedItems?: Array<DonutDataItem>;
  billingSummary?: DataItemBillingSummary;
  currency?: Currency;

  /**
   * The color that will be used for both the dot and the cell in the pie chart.
   * - If provided a string, it will be used for both the dot and the cell.
   * - If provided a tuple, the first member will be used for the dot and the second for the cell.
   * */
  color?: string | readonly [string, string];
  subHeading?: string;
}

export interface GraphMetadata {
  median: number;
  showBilledUnbilled: boolean;
}

export type ReportSelectionKey = 'hourIds' | 'labelIds';
export interface ToggleSelectionParams {
  selectionKey: ReportSelectionKey;
  value?: Array<number> | number;
  batchRemove?: boolean;
}

export enum WidgetDataType {
  Hours = 'hours',
  Money = 'money',
}

export interface UpdateWidgetColumns {
  sort_by?: string;
  order?: 'asc' | 'desc';
  reference?: WidgetReference;
  names?: Array<string>;
}

export interface MoveWidgetParams {
  reference?: WidgetReference;
  targetSequence: number;
}

export enum ExportReportTrackingSource {
  Report = 'saved_report',
  Timesheets = 'timesheets',
  Project = 'project_details_hours',
  User = 'user_details_hours',
  Client = 'client_details',
}

export interface BulkUpdateResponse {
  created_ids: Array<number>;
  deleted_ids: Array<number>;
  updated_ids: Array<number>;
  errors: { create: Array<ExtendedError>; update: Array<ExtendedError> };
}

export type HoursFilterScope = keyof Omit<ReportQuery['filters'], 'billed'> | undefined;

export type ReportResources = 'forecasts' | 'hours';

export type ExportFrequency =
  | 'daily'
  | 'weekly'
  | 'bi_weekly'
  | 'monthly'
  | 'yearly'
  | 'do_not_repeat';

// @todo - generate it from api specs when BE adds it
export type ReportExportSettingsPayload = {
  time_entries: boolean;
  freq: ExportFrequency;
  day: [string] | [number] | [string, number] | null;
  start_date: string | null;
  recipients: string | null;
  hours_column_order: Array<HourColumn>;
  tasks_column_order: Array<TaskColumn>;
  tags_mode: ColumnModeOptions;
  worksheets: Array<ExportWorksheet>;
  format: SupportedExtensions;
  show_excluded_columns: boolean;
};

export type ReportExportSettingsRequestPayload = ReportExportSettingsPayload & {
  time_entries: boolean;
  format: SupportedExtensions;
  resource: 'hours';
  filters: Partial<ReportQuery['filters']>;
  options: {
    version: '1.1';
  };
};

export const DEFAULT_EXPORT_SETTINGS: ReportExportSettingsPayload = {
  time_entries: true,
  freq: 'do_not_repeat',
  day: null,
  start_date: null,
  recipients: null,
  hours_column_order: EMPTY_ARRAY,
  tasks_column_order: EMPTY_ARRAY,
  tags_mode: 'all',
  worksheets: EMPTY_ARRAY,
  format: 'csv',
  show_excluded_columns: false,
};

export const MINIMUM_REPORTS_ON_DASHBOARD = 9;

export const ALL_REPORTS_LIMIT = 15;

export const PUBLIC_REPORTS_LIMIT = 50;
